import { useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { pageOverheadBucketRoots } from "@/lib/manufacturing/overheadBucketTaxonomy";
import type { useManufacturingOverheadPeriodValues } from "./useManufacturingOverheadPeriodValues";

type PeriodValuesState = ReturnType<typeof useManufacturingOverheadPeriodValues>;
const PAGE_SIZES = [5, 10, 15] as const;
type PageSize = (typeof PAGE_SIZES)[number];

function formatPct(value: number | null): string {
  if (value === null) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function formatUsd(value: number | null): string {
  if (value === null) return "—";
  return value.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function ManufacturingOverheadVarianceReportCard({ state }: { state: PeriodValuesState }) {
  const { plantId, periodMonth, varianceReport, listBusy } = state;
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState<PageSize>(5);
  const [pageIndex, setPageIndex] = useState(0);
  const rows = varianceReport?.rows ?? [];
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((row) =>
      row.bucket_name.toLowerCase().includes(needle) || row.bucket_key.toLowerCase().includes(needle),
    );
  }, [rows, query]);
  const page = pageOverheadBucketRoots(filtered, pageIndex, pageSize);

  useEffect(() => {
    setPageIndex(0);
    setQuery("");
  }, [plantId, periodMonth]);

  return (
    <SectionCard className="h-full" title="Variance report" description="Each bucket’s actual against its assumed amount, as a share of the overhead pool.">
      {!plantId ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Select a plant to load the variance report.</div>
      ) : listBusy ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading variance report…</div>
      ) : !varianceReport ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Variance report unavailable.</div>
      ) : (
        <>
          <div className="mb-3 space-y-1 text-xs text-gray-600 dark:text-zinc-400">
            <div>
              Pool actual {formatUsd(varianceReport.overhead_pool_actual_total_usd)} · assumed{" "}
              {formatUsd(varianceReport.overhead_pool_assumed_total_usd)}
            </div>
            <div>
              {varianceReport.alert_count} alert{varianceReport.alert_count === 1 ? "" : "s"} of {varianceReport.rows.length}
              {varianceReport.period_state === "open" ? " · period still open" : ""}
            </div>
          </div>
          <label className="mb-3 block text-xs text-gray-600 dark:text-zinc-400">
            Search
            <input
              type="search"
              value={query}
              placeholder="Bucket name"
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
              onChange={(event) => {
                setQuery(event.target.value);
                setPageIndex(0);
              }}
            />
          </label>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-left text-xs">
              <thead>
                <tr className="border-b border-gray-200 text-gray-600 dark:border-zinc-700 dark:text-zinc-400">
                  <th className="w-[28%] py-1 pr-2 font-medium">Bucket</th>
                  <th className="w-[14%] py-1 pr-2 font-medium">Assumed</th>
                  <th className="w-[14%] py-1 pr-2 font-medium">Actual</th>
                  <th className="w-[14%] py-1 pr-2 font-medium">Variance</th>
                  <th className="w-[14%] py-1 pr-2 font-medium">Pool</th>
                  <th className="w-[16%] py-1 font-medium">Alert</th>
                </tr>
              </thead>
              <tbody>
                {page.page.map((row) => (
                  <tr key={row.bucket_id} className="border-b border-gray-100 dark:border-zinc-800">
                    <td className="truncate py-1 pr-2 font-medium text-gray-900 dark:text-zinc-100" title={row.bucket_name}>
                      {row.bucket_name}
                    </td>
                    <td className="py-1 pr-2">{formatUsd(row.assumed_value_usd)}</td>
                    <td className="py-1 pr-2">{formatUsd(row.actual_value_usd)}</td>
                    <td className="py-1 pr-2">{formatUsd(row.variance_usd)}</td>
                    <td className="py-1 pr-2">{formatPct(row.variance_pct_of_overhead_pool)}</td>
                    <td className="py-1">
                      {row.would_alert ? (
                        <span className="text-red-800 dark:text-red-200">Alert</span>
                      ) : (
                        <span className="text-gray-400 dark:text-zinc-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600 dark:text-zinc-400">
            <span>{page.total === 0 ? "0 buckets" : `${page.rangeStart}–${page.rangeEnd} of ${page.total}`}</span>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1">
                <span>Show</span>
                <select
                  aria-label="Variance report page size"
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 dark:border-zinc-700 dark:bg-zinc-900"
                  value={pageSize}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (next === 5 || next === 10 || next === 15) setPageSize(next);
                    setPageIndex(0);
                  }}
                >
                  {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
              {page.pageCount > 1 ? (
                <>
                  <button type="button" className={ghostBtnClass} disabled={page.pageIndex <= 0} onClick={() => setPageIndex(page.pageIndex - 1)}>Previous</button>
                  <span>{page.pageIndex + 1} / {page.pageCount}</span>
                  <button type="button" className={ghostBtnClass} disabled={page.pageIndex + 1 >= page.pageCount} onClick={() => setPageIndex(page.pageIndex + 1)}>Next</button>
                </>
              ) : null}
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}
